import axios from 'axios';

// Backend API URL - PRODUCTION READY
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 30 second timeout
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// 🔥 AUTH API - COMPLETE BACKEND INTEGRATION
// =============================================================================
export const authAPI = {
  // ✅ SIGNUP - MATCHES BACKEND EXACTLY
  signup: async (userData) => {
    try {
      console.log('🚀 API: Starting signup with:', userData);
      
      const signupData = {
        name: userData.name?.trim(),
        email: userData.email?.toLowerCase().trim(),
        password: userData.password,
        career_goal: userData.career_goal || userData.careerGoal || "",
        preferred_language: userData.preferred_language || userData.preferredLanguage || "en",
        experience_level: userData.experience_level || userData.experienceLevel || "beginner",
        learning_mode: userData.learning_mode || userData.learningMode || "balanced",
        phone: userData.phone || null,
        location: userData.location || null,
      };
      
      console.log('📤 API: Sending signup payload:', signupData);
      const response = await api.post('/auth/signup', signupData);
      
      console.log('✅ API: Signup response:', response.data);
      
      const backendData = response.data;
      const token = backendData.access_token || backendData.token;
      
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(backendData.user || {}));
      }
      
      return {
        success: true,
        token: token,
        user: backendData.user || { name: userData.name, email: userData.email, career_goal: signupData.career_goal },
        message: backendData.message || 'Account created successfully!'
      };
      
    } catch (error) {
      console.error('❌ API: Signup error:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Signup failed');
    }
  },

  // ✅ LOGIN - BACKEND COMPATIBLE
  // ✅ LOGIN - BACKEND COMPATIBLE - FIXED PARAMETER HANDLING
// ✅ LOGIN - FIXED PARAMETER HANDLING
login: async (email, password) => {  // ✅ Individual parameters
  try {
    console.log('🚀 API: Calling /auth/login with:', email);
    
    // ✅ Handle individual parameters safely
    const response = await api.post('/auth/login', {
      email: email.toLowerCase().trim(),  // ✅ Now email is a string
      password: password                  // ✅ Now password is a string
    });
    
    console.log('✅ API: Login response:', response.data);
    
    if (response.data?.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user || {}));
      
      return {
        success: true,
        token: response.data.access_token,
        user: response.data.user || {},
        message: 'Login successful!'
      };
    } else {
      throw new Error('Invalid response from server');
    }
    
  } catch (error) {
    console.error('❌ Auth login error:', error);
    
    let errorMessage = 'Login failed';
    if (error.response?.status === 422) errorMessage = 'Invalid email or password format';
    else if (error.response?.status === 401) errorMessage = 'Invalid email or password';
    else if (error.response?.data?.detail) errorMessage = error.response.data.detail;
    
    throw new Error(errorMessage);
  }
},



  // ✅ GET CURRENT USER
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('❌ Auth getCurrentUser error:', error);
      throw error;
    }
  },

  // ✅ LOGOUT
  logout: async () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRoadmap');
    } catch (error) {
      console.log('Logout error (ignored):', error.message);
    }
  }
};


export const onboardingAPI = {
  // ✅ SUBMIT ONBOARDING - MATCHES BACKEND EXACTLY
  submitOnboarding: async (onboardingData) => {
    try {
      console.log('🚀 API: Starting onboarding submission with:', onboardingData);
      
      // 🔥 CRITICAL: Backend validation expects THESE EXACT FIELD NAMES
      // From error message: "career_goal: Field required, experience_level: Field required"
      const backendPayload = {
        // Backend validation expects underscore versions!
        career_goal: onboardingData.careergoal || onboardingData.careerGoal || "",
        experience_level: onboardingData.experiencelevel || onboardingData.experienceLevel || "beginner",
        current_skills: Array.isArray(onboardingData.currentskills) 
          ? onboardingData.currentskills 
          : Array.isArray(onboardingData.currentSkills) 
            ? onboardingData.currentSkills 
            : [], 
            
        // These might be underscore too based on pattern
        preferred_language: onboardingData.preferredlanguage || onboardingData.preferredLanguage || "en",
        learning_mode: onboardingData.learningmode || onboardingData.learningMode || "balanced",
        timeline_months: parseInt(onboardingData.timelinemonths || onboardingData.timelineMonths || 6),
        resource_preference: onboardingData.resourcepreference || "both",
        resume_text: onboardingData.resumetext || null
      };
      
      console.log('📤 API: Sending corrected payload:', backendPayload);
      
      // Validate required fields
      if (!backendPayload.career_goal) {
        throw new Error('Career goal is required');
      }
      if (!backendPayload.experience_level) {
        throw new Error('Experience level is required');
      }
      
      // Make API call
      const response = await api.post('/onboarding', backendPayload);
      
      console.log('✅ API: Onboarding response:', response.data);
      
      return {
        success: true,
        message: response.data.message || 'Onboarding completed successfully!',
        roadmapId: response.data.roadmapId,
        roadmap: response.data.roadmap,
        aiGenerated: response.data.aiGenerated || true
      };
      
    } catch (error) {
      console.error('❌ API: Onboarding error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        validationErrors: error.response?.data?.detail
      });
      
      // Handle 422 validation errors specifically
      if (error.response?.status === 422) {
        const validationErrors = error.response.data?.detail;
        if (Array.isArray(validationErrors)) {
          const errorMessages = validationErrors.map(err => `${err.loc?.[1]}: ${err.msg}`);
          throw new Error(`Validation Error: ${errorMessages.join(', ')}`);
        } else {
          throw new Error('Validation failed. Please check all required fields.');
        }
      }
      
      throw new Error(error.response?.data?.detail || error.message || 'Onboarding submission failed');
    }
  }
};



// =============================================================================
// 🔥 DASHBOARD API - COMPLETE BACKEND INTEGRATION
// =============================================================================
export const dashboardAPI = {
  // ✅ GET DASHBOARD DATA - MATCHES dashboard.py endpoints
  getDashboard: async () => {
    try {
      console.log('🚀 API: Getting dashboard data');
      const response = await api.get('/dashboard');
      console.log('✅ Dashboard API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ getDashboard error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch dashboard data');
    }
  },

  // ✅ GET USER STATS
  getUserStats: async () => {
    try {
      const response = await api.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('❌ getUserStats error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch user stats');
    }
  },

  // ✅ GET RECENT ACTIVITY
  getRecentActivity: async () => {
    try {
      const response = await api.get('/dashboard/activity');
      return response.data;
    } catch (error) {
      console.error('❌ getRecentActivity error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch recent activity');
    }
  }
};

// =============================================================================
// 🔥 ROADMAP API - MATCHES roadmap.py ENDPOINTS
// =============================================================================
export const roadmapAPI = {
  // ✅ GET USER'S ROADMAPS
  getRoadmaps: async () => {
    try {
      console.log('🚀 API: Getting user roadmaps');
      const response = await api.get('/roadmaps');
      console.log('✅ Roadmaps response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ getRoadmaps error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch roadmaps');
    }
  },

  // ✅ GET SPECIFIC ROADMAP BY ID
  getRoadmapById: async (roadmapId) => {
    try {
      console.log(`🚀 API: Getting roadmap by ID: ${roadmapId}`);
      const response = await api.get(`/roadmaps/${roadmapId}`);
      return response.data;
    } catch (error) {
      console.error('❌ getRoadmapById error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch roadmap details');
    }
  },

  // ✅ UPDATE ROADMAP PROGRESS
  updateProgress: async (roadmapId, progress) => {
    try {
      console.log(`🚀 API: Updating progress for roadmap ${roadmapId}: ${progress}%`);
      const response = await api.put(`/roadmaps/${roadmapId}/progress`, { 
        progress: Math.min(100, Math.max(0, progress)) 
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Update progress error:', error);
      throw error;
    }
  },

  // ✅ UPDATE MODULE PROGRESS
  // Supports both call styles:
  // 1) updateModuleProgress(roadmapId, moduleId, progressData)
  // 2) updateModuleProgress(roadmapId, { moduleId, ...progressData })
  updateModuleProgress: async (roadmapId, moduleIdOrPayload, progressDataArg) => {
    try {
      const moduleId = typeof moduleIdOrPayload === 'object'
        ? moduleIdOrPayload.moduleId
        : moduleIdOrPayload;
      const progressData = typeof moduleIdOrPayload === 'object'
        ? moduleIdOrPayload
        : (progressDataArg || {});

      if (!moduleId) {
        throw new Error('moduleId is required to update module progress');
      }

      console.log(`🚀 API: Updating module ${moduleId} progress:`, progressData);
      const response = await api.put(`/roadmaps/${roadmapId}/modules/${moduleId}/progress`, {
        progress: Math.min(100, Math.max(0, progressData.progress)),
        status: progressData.status || 'current',
        completedHours: progressData.completedHours || 0
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Update module progress error:', error);
      throw error;
    }
  },

  // ✅ MARK MODULE COMPLETE
  markModuleComplete: async (roadmapId, moduleId) => {
    try {
      console.log(`🚀 API: Marking module ${moduleId} complete for roadmap ${roadmapId}`);
      const response = await api.post(`/roadmaps/${roadmapId}/modules/${moduleId}/complete`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Mark module complete error:', error);
      throw error;
    }
  },

  // ✅ GENERATE NEW ROADMAP
  generateRoadmap: async (roadmapData) => {
    try {
      const response = await api.post('/roadmaps/generate', roadmapData);
      return response.data;
    } catch (error) {
      console.error('❌ Generate roadmap error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to generate roadmap');
    }
  }
};

// =============================================================================
// 🔥 ASSESSMENT API - MATCHES assessment.py ENDPOINTS
// =============================================================================
export const assessmentAPI = {
  // ✅ GET MODULE ASSESSMENT
  getModuleAssessment: async (roadmapId, moduleId) => {
    try {
      console.log(`🚀 API: Getting assessment for roadmap ${roadmapId}, module ${moduleId}`);
      const response = await api.get(`/assessment/${roadmapId}/modules/${moduleId}/assessment`);
      return response.data;
    } catch (error) {
      console.error('❌ Get assessment error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch assessment');
    }
  },

  // ✅ SUBMIT ASSESSMENT
  submitAssessment: async (roadmapId, moduleId, submissionData) => {
    try {
      console.log('🚀 API: Submitting assessment:', submissionData);
      const response = await api.post(`/assessment/${roadmapId}/modules/${moduleId}/assessment/submit`, submissionData);
      return response.data;
    } catch (error) {
      console.error('❌ Submit assessment error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to submit assessment');
    }
  },

  // ✅ GET ASSESSMENT ATTEMPTS
  getAssessmentAttempts: async (roadmapId, moduleId) => {
    try {
      const response = await api.get(`/assessment/${roadmapId}/modules/${moduleId}/assessment/attempts`);
      return response.data;
    } catch (error) {
      console.error('❌ Get attempts error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch attempts');
    }
  },

  calculateReadiness: async (payload) => {
    try {
      const response = await api.post('/assessment/calculate-readiness', payload);
      return response.data;
    } catch (error) {
      console.error('❌ Calculate readiness error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to calculate readiness');
    }
  },

  // ✅ REGENERATE ASSESSMENT
  regenerateAssessment: async (roadmapId, moduleId, reason = '') => {
    try {
      const response = await api.post(`/assessment/${roadmapId}/modules/${moduleId}/assessment/regenerate`, { reason });
      return response.data;
    } catch (error) {
      console.error('❌ Regenerate assessment error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to regenerate assessment');
    }
  }
};

// =============================================================================
// 🔥 PROJECT API - MATCHES projects.py ENDPOINTS
// =============================================================================
export const projectAPI = {
  getUserProjects: async () => {
    try {
      console.log('🚀 Getting projects...');
      const response = await api.get('/projects/');
      console.log('✅ Projects:', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch projects');
    }
  },

  // ✅ GET PROJECT BY ID
  getProjectById: async (projectId) => {
    try {
      console.log(`🚀 API: Getting project ${projectId}`);
      const response = await api.get(`/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Get project by ID error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch project details');
    }
  },

  // ✅ GET RECOMMENDED PROJECTS
  getRecommendedProjects: async (roadmapId, moduleId) => {
    try {
      const response = await api.get(`/projects/recommended/${roadmapId}/${moduleId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Get recommended projects error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch recommended projects');
    }
  },

  // ✅ START PROJECT
  startProject: async (projectId) => {
    try {
      const response = await api.post(`/projects/${projectId}/start`);
      return response.data;
    } catch (error) {
      console.error('❌ Start project error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to start project');
    }
  },

  // ✅ SUBMIT PROJECT
  submitProject: async (projectId, submissionData) => {
    try {
      const response = await api.post(`/projects/${projectId}/submit`, submissionData);
      return response.data;
    } catch (error) {
      console.error('❌ Submit project error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to submit project');
    }
  }
};

// =============================================================================
// 🔥 PORTFOLIO API - MATCHES portfolio.py ENDPOINTS
// =============================================================================
export const portfolioAPI = {
  // ✅ GET USER PORTFOLIO
  getPortfolio: async () => {
    try {
      console.log('🚀 API: Getting user portfolio');
      const response = await api.get('/portfolio');
      return response.data;
    } catch (error) {
      console.error('❌ Get portfolio error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch portfolio');
    }
  },

  // ✅ UPDATE PORTFOLIO
  updatePortfolio: async (portfolioData) => {
    try {
      console.log('🚀 API: Updating portfolio');
      const response = await api.put('/portfolio', portfolioData);
      return response.data;
    } catch (error) {
      console.error('❌ Update portfolio error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to update portfolio');
    }
  },

  // ✅ GENERATE PORTFOLIO
  generatePortfolio: async () => {
    try {
      const response = await api.post('/portfolio/generate');
      return response.data;
    } catch (error) {
      console.error('❌ Generate portfolio error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to generate portfolio');
    }
  }
};

// =============================================================================
// 🔥 USER PROFILE API - MATCHES user.py & profile.py ENDPOINTS
// =============================================================================
export const profileAPI = {
  createProfile: async (profileData) => {
    try {
      const response = await api.post('/create-profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Create profile error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to create profile');
    }
  },

  // ✅ GET USER PROFILE
  getProfile: async () => {
  try {
    const response = await api.get('/profile');
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('❌ Get profile error:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch profile');
  }
},

  // ✅ UPDATE USER PROFILE
  updateProfile: async (profileData) => {
    try {
      console.log('🚀 API: Updating user profile:', profileData);
      const response = await api.put('/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('❌ Update profile error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to update profile');
    }
  },

  uploadResume: async (file) => {
    try {
      const formData = new FormData();
      formData.append('resume', file);
      const response = await api.post('/upload-resume', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Upload resume error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to upload resume');
    }
  },

  extractSkills: async (payload) => {
    try {
      const response = await api.post('/extract-skills', payload);
      return response.data;
    } catch (error) {
      console.error('Extract skills error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to extract skills');
    }
  },

  getProfileSkills: async () => {
    try {
      const response = await api.get('/profile/skills');
      return response.data;
    } catch (error) {
      console.error('Get profile skills error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch profile skills');
    }
  },

  addProfileSkill: async (skillName) => {
    try {
      const response = await api.post('/profile/skills', { skill_name: skillName, source: 'manual' });
      return response.data;
    } catch (error) {
      console.error('Add profile skill error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to add skill');
    }
  },

  deleteProfileSkill: async (skillId) => {
    try {
      await api.delete(`/profile/skills/${skillId}`);
      return { success: true };
    } catch (error) {
      console.error('Delete profile skill error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to delete skill');
    }
  },

  updateProfileSkills: async (skills) => {
    try {
      const response = await api.put('/profile/skills', {
        skills: skills.map((skill) => ({
          skill_name: skill.skill_name || skill.name || skill,
          source: skill.source || 'profile',
        })),
      });
      return response.data;
    } catch (error) {
      console.error('Update profile skills error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to update skills');
    }
  },

  // ✅ UPDATE USER SETTINGS
  updateSettings: async (settingsData) => {
    try {
      const response = await api.put('/profile/settings', settingsData);
      return response.data;
    } catch (error) {
      console.error('❌ Update settings error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to update settings');
    }
  },

  getRoleRecommendations: async () => {
    try {
      const response = await api.get('/profile/recommend-roles');
      return response.data;
    } catch (error) {
      console.error('❌ Get role recommendations error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch role recommendations');
    }
  },

  selectRecommendedRole: async (roleId) => {
    try {
      const response = await api.post('/profile/select-role', { role_id: roleId });
      return response.data;
    } catch (error) {
      console.error('❌ Select recommended role error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to save selected role');
    }
  },

  selectCustomRole: async (customRole) => {
    try {
      const response = await api.post('/profile/select-custom-role', { custom_role: customRole });
      return response.data;
    } catch (error) {
      console.error('❌ Select custom role error:', error);
      if (Array.isArray(error.response?.data?.detail)) {
        const message = error.response.data.detail.map((item) => item.msg).join(', ');
        throw new Error(message || 'Failed to save custom role');
      }
      throw new Error(error.response?.data?.detail || 'Failed to save custom role');
    }
  },

  deselectRecommendedRole: async () => {
    try {
      const response = await api.delete('/profile/select-role');
      return response.data;
    } catch (error) {
      console.error('❌ Deselect recommended role error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to clear selected role');
    }
  }
};

// =============================================================================
// 🔥 AI TUTOR API - MATCHES ai.py ENDPOINTS
// =============================================================================
export const aiAPI = {
  // ✅ ASK AI TUTOR
  askTutor: async (question, context = {}) => {
    try {
      console.log('🚀 API: Asking AI tutor:', question);
      const response = await api.post('/ai/tutor', {
        question: question,
        context: context
      });
      return response.data;
    } catch (error) {
      console.error('❌ Ask tutor error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get AI response');
    }
  },

  // ✅ GET LESSON CONTENT
  getLessonContent: async (roadmapId, moduleId, lessonId) => {
    try {
      const response = await api.get(`/ai/lesson/${roadmapId}/${moduleId}/${lessonId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Get lesson content error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch lesson content');
    }
  },

  // ✅ GENERATE EXPLANATION
  generateExplanation: async (topic, difficulty = 'intermediate') => {
    try {
      const response = await api.post('/ai/explain', { topic, difficulty });
      return response.data;
    } catch (error) {
      console.error('❌ Generate explanation error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to generate explanation');
    }
  }
};

export const aiCoachAPI = {
  chat: async ({ message, conversationId = null }) => {
    try {
      const response = await api.post('/ai-coach/chat', {
        message,
        conversation_id: conversationId,
      });
      return response.data;
    } catch (error) {
      console.error('AI coach chat error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get coach response');
    }
  },
};

export const plannerAPI = {
  getToday: async () => {
    try {
      const response = await api.get('/planner/today');
      return response.data;
    } catch (error) {
      console.error('Planner today error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch today planner');
    }
  },

  getWeek: async () => {
    try {
      const response = await api.get('/planner/week');
      return response.data;
    } catch (error) {
      console.error('Planner week error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch weekly planner');
    }
  },

  getMilestones: async () => {
    try {
      const response = await api.get('/planner/milestones');
      return response.data;
    } catch (error) {
      console.error('Planner milestones error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch planner milestones');
    }
  },

  regenerate: async () => {
    try {
      const response = await api.post('/planner/regenerate');
      return response.data;
    } catch (error) {
      console.error('Planner regenerate error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to regenerate planner');
    }
  },

  completeTask: async (taskId) => {
    try {
      const response = await api.post(`/planner/task-complete/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Planner task completion error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to complete planner task');
    }
  },
};

// =============================================================================
// 🔥 NOTIFICATIONS API - MATCHES notifications.py
// =============================================================================
export const notificationAPI = {
  // ✅ GET NOTIFICATIONS
  getNotifications: async () => {
    try {
      const response = await api.get('/notifications');
      return response.data;
    } catch (error) {
      console.error('❌ Get notifications error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch notifications');
    }
  },

  // ✅ MARK NOTIFICATION READ
  markAsRead: async (notificationId) => {
    try {
      const response = await api.put(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('❌ Mark notification read error:', error);
      throw error;
    }
  },

  markAllAsRead: async () => {
    try {
      const response = await api.put('/notifications/read-all');
      return response.data;
    } catch (error) {
      console.error('❌ Mark all notifications read error:', error);
      throw error;
    }
  },

  deleteNotification: async (notificationId) => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Delete notification error:', error);
      throw error;
    }
  },

  syncNotifications: async () => {
    try {
      const response = await api.post('/notifications/sync');
      return response.data;
    } catch (error) {
      console.error('❌ Sync notifications error:', error);
      throw error;
    }
  }
};

// =============================================================================
// 🔥 STATS API - MATCHES stats.py
// =============================================================================
export const statsAPI = {
  // ✅ GET USER STATS
  getUserStats: async () => {
    try {
      const response = await api.get('/stats');
      return response.data;
    } catch (error) {
      console.error('❌ Get user stats error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch user stats');
    }
  },

  // ✅ GET LEARNING ANALYTICS
  getLearningAnalytics: async (timeframe = '30d') => {
    try {
      const response = await api.get(`/stats/analytics?timeframe=${timeframe}`);
      return response.data;
    } catch (error) {
      console.error('❌ Get learning analytics error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch learning analytics');
    }
  }
};





// SETTINGS API - FIXED PATHS
export const settingsAPI = {
  // Get all settings
  getSettings: async () => {
    try {
      console.log('🔍 Fetching settings...');
      // ✅ REMOVE /api PREFIX - baseURL already has it
      const response = await api.get('/settings/');  // NOT /api/settings/
      console.log('✅ Settings loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Get settings error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch settings');
    }
  },

  // Update profile
  updateProfile: async (profileData) => {
    try {
      console.log('💾 Updating profile:', profileData);
      const response = await api.put('/settings/profile', profileData);  // NOT /api/settings/profile
      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Profile updated!'
      };
    } catch (error) {
      console.error('❌ Update profile error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to update profile');
    }
  },

  // Update notifications
  updateNotifications: async (notificationSettings) => {
    try {
      const response = await api.put('/settings/notifications', notificationSettings);
      return {
        success: true,
        data: response.data,
        message: 'Notification settings updated!'
      };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update notifications');
    }
  },
 

  // Update privacy
  updatePrivacy: async (privacySettings) => {
    try {
      const response = await api.put('/settings/privacy', privacySettings);
      return {
        success: true,
        data: response.data,
        message: 'Privacy settings updated!'
      };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update privacy');
    }
  },

  // Update appearance
  updateAppearance: async (appearanceSettings) => {
    try {
      const response = await api.put('/settings/appearance', appearanceSettings);
      return {
        success: true,
        data: response.data,
        message: 'Appearance settings updated!'
      };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update appearance');
    }
  },

  updateLearning: async (learningSettings) => {
    try {
      const response = await api.put('/settings/learning', learningSettings);
      return {
        success: true,
        data: response.data,
        message: 'Learning settings updated!'
      };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update learning settings');
    }
  },

  // Change password
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    try {
      await api.post('/settings/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return {
        success: true,
        message: 'Password changed successfully!'
      };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to change password');
    }
  },

  // Delete account
  deleteAccount: async (confirmationPassword) => {
    try {
      await api.delete('/settings/delete-account', {
        data: { confirmation_password: confirmationPassword }
      });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return {
        success: true,
        message: 'Account deleted successfully.'
      };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to delete account');
    }
  },

  // ✅ UNIFIED UPDATE METHOD - HANDLES ALL CATEGORIES
  updateSettings: async (category, updateData) => {
    try {
      console.log(`💾 API: Updating ${category} settings:`, updateData);

      // Route to appropriate method based on category
      switch (category) {
        case 'profile':
          return await settingsAPI.updateProfile(updateData);
        case 'notifications':
          return await settingsAPI.updateNotifications(updateData);
        case 'privacy':
          return await settingsAPI.updatePrivacy(updateData);
        case 'appearance':
          return await settingsAPI.updateAppearance(updateData);
        case 'learning':
          return await settingsAPI.updateLearning(updateData);
        default:
          throw new Error(`Unknown settings category: ${category}`);
      }
    } catch (error) {
      console.error(`❌ Update ${category} settings error:`, error);
      throw error;
    }
  },

  // ✅ EXPORT USER DATA
  exportData: async (exportOptions = {}) => {
    try {
      console.log('📤 API: Exporting user data...');
      const response = await api.post('/api/settings/export-data', {
        export_format: exportOptions.format || 'json',
        include_learning_data: exportOptions.includeLearning !== false,
        include_personal_data: exportOptions.includePersonal !== false,
        include_settings: exportOptions.includeSettings !== false,
        email_when_ready: exportOptions.emailReady !== false
      });

      return {
        success: true,
        exportId: response.data.export_id,
        downloadUrl: response.data.download_url,
        message: 'Data export initiated successfully!'
      };
    } catch (error) {
      console.error('❌ Export data error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to export data');
    }
  },

  // ✅ GET ACCOUNT INFO
  getAccountInfo: async () => {
    try {
      const response = await api.get('/api/settings/account-info');
      return response.data;
    } catch (error) {
      console.error('❌ Get account info error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch account info');
    }
  }
};

export const notesAPI = {
  getModuleNotes: async (roadmapId, moduleId) => {
    try {
      const response = await api.get(`/notes/roadmaps/${roadmapId}/modules/${moduleId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch notes');
    }
  },

  saveModuleNotes: async (roadmapId, moduleId, content) => {
    try {
      const response = await api.put(`/notes/roadmaps/${roadmapId}/modules/${moduleId}`, { content });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to save notes');
    }
  }
};

// 🆕 LEARNING PROGRESS API
export const progressAPI = {
  // Get complete progress data for a roadmap
  getRoadmapProgress: async (roadmapId) => {
    try {
      console.log(`🚀 API: Getting progress for roadmap ${roadmapId}`);
      const response = await api.get(`/progress/roadmap/${roadmapId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Get progress error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch progress');
    }
  },

  // Update time spent on a module
  updateTimeSpent: async (roadmapId, moduleId, formatType, minutes) => {
    try {
      console.log(`🚀 API: Updating time spent for module ${moduleId}`);
      const response = await api.post(
        `/progress/roadmap/${roadmapId}/module/${moduleId}/time-spent`,
        { format_type: formatType, minutes }
      );
      return response.data;
    } catch (error) {
      console.error('❌ Update time spent error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to update time spent');
    }
  },

  // Update skill progress for a module
  updateSkillProgress: async (roadmapId, moduleId, skillName, progressPercentage, status = 'learning') => {
    try {
      console.log(`🚀 API: Updating skill progress for module ${moduleId}`);
      const response = await api.post(
        `/progress/roadmap/${roadmapId}/module/${moduleId}/skill-progress`,
        { skill_name: skillName, progress_percentage: progressPercentage, status }
      );
      return response.data;
    } catch (error) {
      console.error('❌ Update skill progress error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to update skill progress');
    }
  },

  // Create a progress snapshot
  createSnapshot: async (roadmapId, triggerEvent = 'manual') => {
    try {
      console.log(`🚀 API: Creating progress snapshot for roadmap ${roadmapId}`);
      const response = await api.post(
        `/progress/roadmap/${roadmapId}/snapshot`,
        { trigger_event: triggerEvent }
      );
      return response.data;
    } catch (error) {
      console.error('❌ Create snapshot error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to create snapshot');
    }
  },

  // Get progress history
  getProgressHistory: async (roadmapId, limit = 10) => {
    try {
      console.log(`🚀 API: Getting progress history for roadmap ${roadmapId}`);
      const response = await api.get(`/progress/roadmap/${roadmapId}/history?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('❌ Get history error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch progress history');
    }
  }
};

// Export default api instance
export default api;

// 🎯 HELPER FUNCTIONS FOR COMMON OPERATIONS
export const apiHelpers = {
  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Get stored user data
  getCurrentUser: () => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  },

  // Clear all auth data
  clearAuthData: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRoadmap');
  }
};

console.log('🔥 API Services loaded successfully with comprehensive backend integration!');
